import React, { useMemo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Typography, Button, Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import RHFTextField from "./components/RHFTextField";
import RHFSelect from "./components/RHFSelect";
import RHFCheckbox from "./components/RHFCheckbox";
import RHSRadio from "./components/RHFRadio";
import RHFDatePicker from "./components/RHFDatePicker";
import RHFAutoAddressField from "./components/RHFAutoAddressField";
import {
    fetchOffering,
    sendFormInput,
    schema,
    states,
    cyclonicStates,
    boltToSlabOptions,
    deliveryOptions,
    defaultValues,
    canadaGibsonFont,
    addTradeMark,
    minDeliveryDate,
    formatDate,
    useBrandTheme,
    brand,
} from "./Config";
import ReCAPTCHA from "react-google-recaptcha";

const brandTheme = useBrandTheme(brand);

export default function CarportOrderForm() {
    const [offering, setOffering] = useState(null);
    const [designTypes, setDesignTypes] = useState([]);
    const [validDesignTypes, setValidDesignTypes] = useState([]);
    const [stateHasCyclonicRegions, setStateHasCyclonicRegions] = useState(true);
    const [formLocked, setFormLocked] = useState(false);
    const [submittedSuccess, setSubmittedSuccess] = useState(false);
    const [fetchingOffering, setFetchingOffering] = useState(false);
    const [fetchOfferingErrorMessage, setFetchOfferingErrorMessage] = useState("");
    const [sendInputErrorMessage, setSendInputErrorMessage] = useState("");
    const [captchaToken, setCaptchaToken] = useState(null);
    const [sendingOrder, setSendingOrder] = useState(false);
    const captchaRef = React.useRef();

    const { control, handleSubmit, watch, reset, setValue, errors, trigger } = useForm({
        mode: "onBlur",
        resolver: yupResolver(schema),
        defaultValues: defaultValues,
    });

    const selectedDesignType = watch("design.carportType");
    const selectedRoofSheet = watch("design.roofSheet");
    const selectedGutter = watch("design.gutter");
    const selectedState = watch("site.state");
    const isCyclonic = watch("site.isCyclonic") === "Yes";
    const isBusinessUser = watch("isBusinessUser");

    const beamsAndPostsColourOptions = useMemo(() => {
        const colours = offering?.Posts?.colours || [];
        return colours.map((colour) => addTradeMark(colour));
    }, [offering]);

    const flashingColourOptions = useMemo(() => {
        const colours = offering?.Flashings?.colours || [];
        return colours.map((colour) => addTradeMark(colour));
    }, [offering]);

    const roofColourOptions = useMemo(() => {
        const colours = offering?.RoofSheet?.[selectedRoofSheet]?.colours || [];
        return colours.map((colour) => addTradeMark(colour));
    }, [offering, selectedRoofSheet]);

    const gutterColourOptions = useMemo(() => {
        const colours = offering?.Gutters?.[selectedGutter]?.colours || [];
        return colours.map((colour) => addTradeMark(colour));
    }, [offering, selectedGutter]);

    const roofSheetOptions = useMemo(() => getRoofSheetOptions(), [offering, selectedDesignType]);

    useEffect(() => {
        setValidDesignTypes([]);
        if (!selectedState || selectedState === "") return;

        const abort = new AbortController();

        setFetchingOffering(true);

        fetchOffering(selectedState, abort.signal)
            .then((res) => {
                setOffering(res?.design_options?.["National Carport"]);
                const carportTypes =
                    res.DesignType.find((item) => typeof item === "object" && item["National Carport"])?.[
                        "National Carport"
                    ] ?? [];

                if (carportTypes?.length > 0) {
                    setFetchOfferingErrorMessage("");
                } else {
                    setFetchOfferingErrorMessage(`Carports are not available in ${selectedState}`);
                }

                setFetchingOffering(false);
                setDesignTypes(carportTypes);
            })
            .catch((err) => {
                if (err !== "Aborted") {
                    setFetchOfferingErrorMessage(`Carports are not available in ${selectedState}`);
                    setFetchingOffering(false);
                    setValue("site.state", "");
                }
            });

        return () => {
            abort.abort("Aborted");
        };
    }, [selectedState]);

    useEffect(() => {
        if (!(selectedRoofSheet in roofSheetOptions) && selectedRoofSheet !== "") {
            setValue("design.roofSheet", "");
        }

        if (roofColourOptions.length === 0 && selectedRoofSheet !== "") {
            setValue("design.roofSheetColour", "");
        }
    }, [roofSheetOptions, selectedRoofSheet, setValue]);

    useEffect(() => {
        if (!selectedState) return;

        const hasCyclonicRegions = cyclonicStates.includes(selectedState);
        setStateHasCyclonicRegions(hasCyclonicRegions);

        if (hasCyclonicRegions && isCyclonic) {
            setValue("site.isCyclonic", "Yes");
        } else if (!hasCyclonicRegions && isCyclonic) {
            setValue("site.isCyclonic", "No");
        }
    }, [selectedState, cyclonicStates, setStateHasCyclonicRegions, setValue]);

    useEffect(() => {
        if (!Array.isArray(designTypes)) return;

        const hasNonCyclonicOptions = designTypes.some((type) => type.toLowerCase().includes("non-cyclonic"));
        const hasCyclonicOptions = designTypes.some(
            (type) => !type.toLowerCase().includes("non-cyclonic") && type.toLowerCase().includes("cyclonic")
        );

        const shouldFilter = (isCyclonic && hasNonCyclonicOptions) || (!isCyclonic && hasCyclonicOptions);

        if (shouldFilter) {
            const filteredDesignTypes = designTypes.filter((type) =>
                isCyclonic ? !type.toLowerCase().includes("non-cyclonic") : type.toLowerCase().includes("non-cyclonic")
            );

            setValidDesignTypes(filteredDesignTypes);
            setValue("design.carportType", "");
        }
    }, [isCyclonic, designTypes, setDesignTypes, setValue, setValidDesignTypes]);

    function getRoofSheetOptions() {
        if (!offering?.RoofSheet || !selectedDesignType) return {};

        return Object.keys(offering.RoofSheet).reduce((options, key) => {
            const conditions = offering.RoofSheet[key].conditions;

            if (checkCondition("SubDesignType", conditions)) {
                options[key] = offering.RoofSheet[key];
            }

            return options;
        }, {});
    }

    function checkCondition(key, conditions) {
        const conditionValue = conditions?.[key];
        if (!conditionValue || typeof conditionValue !== "string") return true;

        const negate = conditionValue.startsWith("!");
        const [, expected] = conditionValue.replace(/^!/, "").split(":");

        const normalizedDesignTypes = selectedDesignType.toLowerCase();
        const normalizedExpected = expected.toLowerCase();

        const contains = normalizedDesignTypes.includes(normalizedExpected);

        return negate ? !contains : contains;
    }

    function resetAndUnlockForm() {
        reset();
        captchaRef.current.reset();
        setCaptchaToken(null);
        setStateHasCyclonicRegions(true);
        setSubmittedSuccess(false);
        setFormLocked(false);
        setFetchOfferingErrorMessage("");
        setSendInputErrorMessage("");
        setOffering(null);
        setDesignTypes([]);
        setValidDesignTypes([]);
        setFetchingOffering(false);
    }

    function onSubmit(data, e) {
        if (!captchaToken) {
            alert("Please complete the CAPTCHA.");
            return;
        }

        const deliveryDateString = formatDate(data.delivery.preferredDate);

        const dataWithCaptcha = {
            ...data,
            delivery: {
                ...data.delivery,
                preferredDate: deliveryDateString,
            },
            captchaToken,
        };

        setFormLocked(true);
        setSendingOrder(true);
        setSendInputErrorMessage("");

        sendFormInput(dataWithCaptcha)
            .then(() => {
                setSendingOrder(false);
                setSendInputErrorMessage();
                setSubmittedSuccess(true);
            })
            .catch((err) => {
                setFormLocked(false);
                setSendingOrder(false);
                setSubmittedSuccess(false);
                setSendInputErrorMessage(
                    typeof err === "string"
                        ? err
                        : err?.message || "An unexpected error occurred. Please contact Lysaght for assistance."
                );
            });
    }

    function onError(errors, e) {
        console.error(errors, e);
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit, onError)} noValidate style={{ maxWidth: 1000, margin: "auto" }}>
                <FormHeader />
                <Section title="">
                    <Grid container spacing={2} item sm={12}>
                        <Grid container item sm={12}>
                            <Grid item xs={12}>
                                <RHFCheckbox
                                    control={control}
                                    name="isBusinessUser"
                                    label="I am a business"
                                    disabled={formLocked}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Section>
                {isBusinessUser && (
                    <Section title="Business">
                        <Grid container spacing={2} item sm={12}>
                            <Grid item xs={12}>
                                <RHFTextField
                                    control={control}
                                    name="business.name"
                                    label="Business name *"
                                    inputProps={{ maxLength: 100 }}
                                    errors={errors}
                                    disabled={formLocked}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RHFTextField
                                    control={control}
                                    name="business.contactName"
                                    label="Contact name *"
                                    inputProps={{ maxLength: 60 }}
                                    errors={errors}
                                    disabled={formLocked}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <RHFTextField
                                    control={control}
                                    name="business.phone"
                                    label="Phone *"
                                    inputProps={{ maxLength: 16 }}
                                    errors={errors}
                                    disabled={formLocked}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <RHFTextField
                                    control={control}
                                    name="business.email"
                                    label="Email *"
                                    inputProps={{ maxLength: 60 }}
                                    errors={errors}
                                    disabled={formLocked}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <RHFTextField
                                    control={control}
                                    name="business.accountCode"
                                    label="Lysaght account code (if known)"
                                    inputProps={{ maxLength: 20 }}
                                    errors={errors}
                                    disabled={formLocked}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <RHFTextField
                                    control={control}
                                    name="business.purchaseOrderNo"
                                    label="Purchase order number"
                                    inputProps={{ maxLength: 20 }}
                                    errors={errors}
                                    disabled={formLocked}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Section>
                )}
                <Section title="Customer">
                    <Grid container spacing={2} item sm={12}>
                        <Grid item xs={12} sm={6}>
                            <RHFTextField
                                control={control}
                                name="customer.firstName"
                                label="First name *"
                                inputProps={{ maxLength: 50 }}
                                errors={errors}
                                disabled={formLocked}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RHFTextField
                                control={control}
                                name="customer.lastName"
                                label="Last name *"
                                inputProps={{ maxLength: 50 }}
                                errors={errors}
                                disabled={formLocked}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RHFTextField
                                control={control}
                                name="customer.phone"
                                label="Phone *"
                                inputProps={{ maxLength: 16 }}
                                errors={errors}
                                disabled={formLocked}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RHFTextField
                                control={control}
                                name="customer.email"
                                label="Email *"
                                inputProps={{ maxLength: 60 }}
                                errors={errors}
                                disabled={formLocked}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Section>
                <Section title="Site">
                    <Grid container spacing={2} item sm={12}>
                        <Grid item xs={12}>
                            <RHFAutoAddressField
                                control={control}
                                setValue={setValue}
                                name="site.streetAddress"
                                label="Street address *"
                                inputProps={{ maxLength: 200 }}
                                errors={errors}
                                disabled={formLocked}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RHFSelect
                                control={control}
                                name="site.state"
                                label="State *"
                                options={states}
                                disabled={formLocked}
                                errors={errors}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <RHFSelect
                                control={control}
                                name="site.isCyclonic"
                                label="Is this a cyclonic region? *"
                                defaultValue="No"
                                options={["Yes", "No"]}
                                disabled={!stateHasCyclonicRegions || formLocked}
                                errors={errors}
                            />
                        </Grid>
                    </Grid>
                </Section>
                <Section title="Design">
                    {validDesignTypes?.length > 0 ? (
                        <Grid container spacing={2} item sm={12}>
                            <Grid item xs={12}>
                                <RHFSelect
                                    control={control}
                                    name="design.carportType"
                                    label="Carport type *"
                                    options={validDesignTypes}
                                    disabled={formLocked}
                                    errors={errors}
                                />
                            </Grid>
                            {selectedDesignType?.length > 0 && (
                                <>
                                    <Grid item sm={12}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                mt: 2,
                                                color: brandTheme?.primary,
                                                fontWeight: "600",
                                                ...canadaGibsonFont,
                                            }}
                                        >
                                            ROOFING
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <RHFSelect
                                            control={control}
                                            name="design.roofSheet"
                                            label="Roof sheeting *"
                                            options={roofSheetOptions}
                                            disabled={formLocked}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {roofColourOptions?.length > 0 && (
                                            <RHFSelect
                                                control={control}
                                                name="design.roofSheetColour"
                                                label="Roof colour *"
                                                options={roofColourOptions}
                                                disabled={formLocked}
                                                errors={errors}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <RHFSelect
                                            control={control}
                                            name="design.gutter"
                                            label="Gutter *"
                                            options={offering?.Gutters}
                                            disabled={formLocked}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {gutterColourOptions?.length > 0 && (
                                            <RHFSelect
                                                control={control}
                                                name="design.gutterColour"
                                                label="Gutter colour *"
                                                options={gutterColourOptions}
                                                disabled={formLocked}
                                                errors={errors}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {flashingColourOptions?.length > 0 && (
                                            <RHFSelect
                                                control={control}
                                                name="design.flashingColour"
                                                label="Flashing colour *"
                                                options={flashingColourOptions}
                                                disabled={formLocked}
                                                errors={errors}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RHFCheckbox
                                            control={control}
                                            name="design.includeDownpipe"
                                            label="Include PVC Downpipe"
                                            disabled={formLocked}
                                            sx={{ ml: 4 }}
                                        />
                                    </Grid>
                                    <Grid item sm={12}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                mt: 2,
                                                color: brandTheme?.primary,
                                                fontWeight: "600",
                                                ...canadaGibsonFont,
                                            }}
                                        >
                                            BEAMS & POSTS
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {beamsAndPostsColourOptions?.length > 0 && (
                                            <RHFSelect
                                                control={control}
                                                name="design.frameColour"
                                                label="Beams & posts colour *"
                                                options={beamsAndPostsColourOptions}
                                                disabled={formLocked}
                                                errors={errors}
                                            />
                                        )}
                                    </Grid>
                                    {!isCyclonic && (
                                        <>
                                            <Grid item sm={12}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        mt: 2,
                                                        color: brandTheme?.primary,
                                                        fontWeight: "600",
                                                        ...canadaGibsonFont,
                                                    }}
                                                >
                                                    FOOTINGS
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <RHSRadio
                                                    control={control}
                                                    name="design.boltToSlabFootingCount"
                                                    label="Bolted to Slab Footings"
                                                    options={boltToSlabOptions}
                                                    disabled={formLocked}
                                                    defaultValue={0}
                                                    sx={{ ml: 4 }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </>
                            )}
                        </Grid>
                    ) : (
                        <Grid item xs={12} sx={{ height: "70px" }}>
                            {fetchingOffering ? (
                                <LinearProgress sx={{ mt: "10px" }} />
                            ) : fetchOfferingErrorMessage?.length > 0 ? (
                                <Alert severity="error" sx={canadaGibsonFont}>
                                    {fetchOfferingErrorMessage}
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={canadaGibsonFont}>
                                    Select a state to begin customising your order.
                                </Alert>
                            )}
                        </Grid>
                    )}
                </Section>
                <Section title="Delivery">
                    <Grid container spacing={2} item sm={12}>
                        <Grid item xs={12} sm={5}>
                            <RHSRadio
                                control={control}
                                name="delivery.Location"
                                label=""
                                options={deliveryOptions}
                                disabled={formLocked}
                                defaultValue={"Site"}
                                sx={{ ml: 4 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <RHFDatePicker
                                name="delivery.preferredDate"
                                label="Preferred delivery date *"
                                control={control}
                                errors={errors}
                                disabled={formLocked}
                                defaultValue={minDeliveryDate}
                                trigger={trigger}
                            />
                            <Alert severity="info" sx={{ ...canadaGibsonFont, mt: 2 }}>
                                Delivery charges may apply depending on delivery location.
                            </Alert>
                        </Grid>
                    </Grid>
                </Section>
                <Section title="Notes">
                    <Grid container spacing={2} item sm={12}>
                        <Grid item xs={12}>
                            <RHFTextField
                                control={control}
                                name="notes.text"
                                label="Optional notes"
                                placeholder="Any additional notes for our team (max 400 characters)"
                                fullWidth
                                multiline
                                rows={4}
                                inputProps={{ maxLength: 400 }}
                                disabled={formLocked}
                                errors={errors}
                            />
                        </Grid>
                    </Grid>
                </Section>
                <Section title="">
                    <Grid container spacing={2} alignItems={"center"}>
                        {selectedDesignType?.length > 0 && (
                            <Grid item xs={12} sx={{ height: "70px" }}>
                                <Alert severity="info" sx={canadaGibsonFont}>
                                    This enquiry is subject to review and acceptance by Lysaght.
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <ReCAPTCHA
                                sitekey="6Le1lKUrAAAAAB_1A5oSZLyAJnXWdEQlPzP5968L"
                                onChange={(token) => setCaptchaToken(token)}
                                ref={captchaRef}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={formLocked || validDesignTypes < 1 || !captchaToken}
                                sx={{
                                    backgroundColor: "#00234B",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#025a99",
                                    },
                                    ...canadaGibsonFont,
                                }}
                            >
                                Submit
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="outlined" sx={canadaGibsonFont} onClick={() => resetAndUnlockForm()}>
                                {submittedSuccess ? "New Order" : "Reset"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sx={{ height: "70px" }}>
                            {submittedSuccess && (
                                <Alert severity="success" sx={canadaGibsonFont}>
                                    Thank you, your order has been submitted. A representative from our team will be in
                                    touch.
                                </Alert>
                            )}

                            {!submittedSuccess && Object.keys(errors)?.length > 0 && (
                                <Alert severity="error" sx={canadaGibsonFont}>
                                    Please fix the highlighted errors before submitting.
                                </Alert>
                            )}

                            {!submittedSuccess && sendInputErrorMessage?.length > 0 && (
                                <Alert severity="error" sx={canadaGibsonFont}>
                                    {sendInputErrorMessage}
                                </Alert>
                            )}

                            {!submittedSuccess && !sendInputErrorMessage?.length > 0 && sendingOrder && (
                                <LinearProgress sx={{ mt: "10px" }} />
                            )}
                        </Grid>
                    </Grid>
                </Section>
                <FormFooter />
            </form>
        </>
    );
}

const FormFooter = () => {
    const wrapperStyle = {
        position: "relative",
        width: "100%",
        height: brandTheme.footerPoly ? "" : `clamp(50px, 8vw, ${brandTheme.footerMaxHeight || 200}px)`,
        overflow: "hidden",
    };

    const shapeStyle = {
        width: "100%",
        height: brandTheme.footerPoly ? undefined : "100%",
        marginTop: brandTheme.footerPoly ? "3vw" : undefined,
        aspectRatio: brandTheme.footerPoly ? "5 / 1.2" : undefined,
        backgroundColor: brandTheme.footerColour,
        clipPath: brandTheme.footerPoly,
    };

    const textBlockStyle = {
        display: "flex",
        alignItems: "flex-end",
        position: "absolute",
        bottom: "15%",
        right: "0vw",
        zIndex: 1001,
    };

    return (
        <div style={wrapperStyle}>
            <div style={shapeStyle} />
            <div style={textBlockStyle}>
                <Typography
                    sx={{
                        color: "white",
                        fontWeight: "600",
                        fontSize: { xs: "0.5rem", sm: "0.8rem", md: "1rem" },
                        lineHeight: 1,
                        fontFamily: "canada-type-gibson",
                        marginRight: { xs: 2, sm: 4, md: 6 },
                    }}
                >
                    {brandTheme.slogan}
                </Typography>
            </div>
        </div>
    );
};

const FormHeader = () => {
    const wrapperStyle = {
        position: "relative",
        width: "100%",
        height: "40vw",
        maxHeight: 360,
        background: brandTheme.headerGradient,
        overflow: "hidden",
    };

    const shapeStyle = {
        position: "relative",
        width: "100%",
        aspectRatio: "5 / 1.2",
        backgroundColor: "white",
        clipPath: "polygon(0% 80%, 85% 40%, 100% 50%, 100% 100%, 0% 100%)",
        transform: "scale(-1, -1)",
        zIndex: 999,
        pointerEvents: "none",
        display: brandTheme.headerShape,
    };

    const imageStyle = {
        position: "absolute",
        ...(brandTheme?.logoPlacement ?? {}),
        zIndex: 1000,
        pointerEvents: "auto",
    };

    const textBlockStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        position: "absolute",
        bottom: "1vw",
        right: 0,
        zIndex: 1001,
    };

    return (
        <div style={wrapperStyle}>
            {brandTheme.logo && <img src={brandTheme.logo} alt={`${brandTheme.name} logo`} style={imageStyle} />}
            <div style={shapeStyle} />
            <div style={textBlockStyle}>
                <Typography
                    sx={{
                        color: "white",
                        fontWeight: "bolder",
                        fontSize: { xs: "2rem", sm: "3rem", md: "3.5rem" },
                        lineHeight: 1,
                        fontFamily: "canada-type-gibson",
                        marginRight: { xs: 2, sm: 4, md: 6 },
                    }}
                >
                    NATIONAL CARPORT
                </Typography>

                <Typography
                    sx={{
                        color: "white",
                        fontWeight: "bolder",
                        fontSize: { xs: "1.2rem", sm: "2rem", md: "2rem" },
                        fontFamily: "canada-type-gibson",
                        marginRight: { xs: 2, sm: 4, md: 6 },
                    }}
                >
                    ORDER FORM
                </Typography>
            </div>
        </div>
    );
};

function Section({ title, children }) {
    return (
        <Box
            sx={{
                mt: 4,
                width: "95%",
                mx: "auto",
            }}
        >
            <Grid container rowSpacing={2}>
                <Grid container item sm={2}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: brandTheme.primary,
                            fontFamily: "canada-type-gibson",
                            fontWeight: 600,
                        }}
                    >
                        {title.toUpperCase()}
                    </Typography>
                </Grid>
                <Grid container item sm={10}>
                    {children}
                </Grid>
            </Grid>
        </Box>
    );
}
